import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getAllDemoData, generateId, DEMO_USER } from '@/data/demoData';

/**
 * DemoContext - Fully Interactive Demo Mode
 * 
 * Provides a complete demo experience where:
 * - All data is stored in memory/localStorage (never hits the backend)
 * - Full CRUD operations work exactly like real data
 * - Exiting demo mode discards all changes
 * - The Carter family data is pre-loaded
 */

const DEMO_STORAGE_KEY = 'forgetmenot_demo_data';
const DEMO_MODE_KEY = 'forgetmenot_demo_mode';

const DemoContext = createContext(null);

export const useDemo = () => {
  const context = useContext(DemoContext);
  if (!context) {
    throw new Error('useDemo must be used within a DemoProvider');
  }
  return context;
};

export const DemoProvider = ({ children }) => {
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [demoData, setDemoData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize demo state from localStorage on mount
  useEffect(() => {
    const savedDemoMode = localStorage.getItem(DEMO_MODE_KEY) === 'true';
    if (savedDemoMode) {
      const savedData = localStorage.getItem(DEMO_STORAGE_KEY);
      if (savedData) {
        try {
          setDemoData(JSON.parse(savedData));
          setIsDemoMode(true);
        } catch (e) {
          console.error('Failed to parse demo data:', e);
          // Reset if corrupted
          localStorage.removeItem(DEMO_STORAGE_KEY);
          localStorage.removeItem(DEMO_MODE_KEY);
        }
      } else {
        // Demo mode was set but no data - initialize fresh
        const freshData = getAllDemoData();
        setDemoData(freshData);
        localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(freshData));
        setIsDemoMode(true);
      }
    }
    setLoading(false);
  }, []);

  // Save demo data to localStorage whenever it changes
  useEffect(() => {
    if (isDemoMode && demoData) {
      localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(demoData));
    }
  }, [isDemoMode, demoData]);

  // ============= DEMO MODE CONTROL =============

  const enterDemoMode = useCallback(() => {
    const freshData = getAllDemoData();
    setDemoData(freshData);
    setIsDemoMode(true);
    localStorage.setItem(DEMO_MODE_KEY, 'true');
    localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(freshData));
  }, []);

  const exitDemoMode = useCallback(() => {
    setIsDemoMode(false);
    setDemoData(null);
    localStorage.removeItem(DEMO_MODE_KEY);
    localStorage.removeItem(DEMO_STORAGE_KEY);
  }, []);

  const resetDemoData = useCallback(() => {
    const freshData = getAllDemoData();
    setDemoData(freshData);
    localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(freshData));
  }, []);

  // ============= GENERIC CRUD HELPERS =============

  const updateDemoData = useCallback((updater) => {
    setDemoData(prev => {
      const newData = updater(prev);
      return newData;
    });
  }, []);

  // Generic add item to array
  const addItem = useCallback((collection, item) => {
    const newItem = {
      ...item,
      id: item.id || generateId(collection),
      user_id: DEMO_USER.id,
      created_at: new Date().toISOString()
    };
    
    updateDemoData(prev => ({
      ...prev,
      [collection]: [...(prev[collection] || []), newItem]
    }));
    
    return newItem;
  }, [updateDemoData]);

  // Generic update item in array
  const updateItem = useCallback((collection, id, updates) => {
    let updatedItem = null;
    
    updateDemoData(prev => ({
      ...prev,
      [collection]: (prev[collection] || []).map(item => {
        if (item.id === id) {
          updatedItem = { ...item, ...updates, updated_at: new Date().toISOString() };
          return updatedItem;
        }
        return item;
      })
    }));
    
    return updatedItem;
  }, [updateDemoData]);

  // Generic delete item from array
  const deleteItem = useCallback((collection, id) => {
    updateDemoData(prev => ({
      ...prev,
      [collection]: (prev[collection] || []).filter(item => item.id !== id)
    }));
  }, [updateDemoData]);

  // Generic get items from array
  const getItems = useCallback((collection) => {
    return demoData?.[collection] || [];
  }, [demoData]);

  // Generic get single item
  const getItem = useCallback((collection, id) => {
    return (demoData?.[collection] || []).find(item => item.id === id);
  }, [demoData]);

  // ============= SPECIALIZED DATA ACCESSORS =============

  // User
  const getDemoUser = useCallback(() => {
    return demoData?.user || DEMO_USER;
  }, [demoData]);

  // Medical Profile
  const getMedicalProfile = useCallback(() => {
    return demoData?.medicalProfile || {};
  }, [demoData]);

  const updateMedicalProfile = useCallback((updates) => {
    updateDemoData(prev => ({
      ...prev,
      medicalProfile: { ...prev.medicalProfile, ...updates }
    }));
    return { ...demoData?.medicalProfile, ...updates };
  }, [demoData, updateDemoData]);

  // Profiles
  const getProfiles = useCallback(() => getItems('profiles'), [getItems]);
  const addProfile = useCallback((profile) => addItem('profiles', profile), [addItem]);
  const updateProfile = useCallback((id, updates) => updateItem('profiles', id, updates), [updateItem]);
  const deleteProfile = useCallback((id) => deleteItem('profiles', id), [deleteItem]);

  // Allergies
  const getAllergies = useCallback(() => getItems('allergies'), [getItems]);
  const addAllergy = useCallback((allergy) => addItem('allergies', allergy), [addItem]);
  const updateAllergy = useCallback((id, updates) => updateItem('allergies', id, updates), [updateItem]);
  const deleteAllergy = useCallback((id) => deleteItem('allergies', id), [deleteItem]);

  // Medications
  const getMedications = useCallback(() => getItems('medications'), [getItems]);
  const addMedication = useCallback((medication) => addItem('medications', medication), [addItem]);
  const updateMedication = useCallback((id, updates) => updateItem('medications', id, updates), [updateItem]);
  const deleteMedication = useCallback((id) => deleteItem('medications', id), [deleteItem]);

  // Conditions
  const getConditions = useCallback(() => getItems('conditions'), [getItems]);
  const addCondition = useCallback((condition) => addItem('conditions', condition), [addItem]);
  const updateCondition = useCallback((id, updates) => updateItem('conditions', id, updates), [updateItem]);
  const deleteCondition = useCallback((id) => deleteItem('conditions', id), [deleteItem]);

  // Surgeries
  const getSurgeries = useCallback(() => getItems('surgeries'), [getItems]);
  const addSurgery = useCallback((surgery) => addItem('surgeries', surgery), [addItem]);
  const updateSurgery = useCallback((id, updates) => updateItem('surgeries', id, updates), [updateItem]);
  const deleteSurgery = useCallback((id) => deleteItem('surgeries', id), [deleteItem]);

  // Doctors
  const getDoctors = useCallback(() => getItems('doctors'), [getItems]);
  const addDoctor = useCallback((doctor) => addItem('doctors', doctor), [addItem]);
  const updateDoctor = useCallback((id, updates) => updateItem('doctors', id, updates), [updateItem]);
  const deleteDoctor = useCallback((id) => deleteItem('doctors', id), [deleteItem]);

  // Insurance
  const getInsurance = useCallback(() => getItems('insurance'), [getItems]);
  const addInsurance = useCallback((insurance) => addItem('insurance', insurance), [addItem]);
  const updateInsurance = useCallback((id, updates) => updateItem('insurance', id, updates), [updateItem]);
  const deleteInsurance = useCallback((id) => deleteItem('insurance', id), [deleteItem]);

  // Emergency Contacts
  const getEmergencyContacts = useCallback(() => getItems('emergencyContacts'), [getItems]);
  const addEmergencyContact = useCallback((contact) => addItem('emergencyContacts', contact), [addItem]);
  const updateEmergencyContact = useCallback((id, updates) => updateItem('emergencyContacts', id, updates), [updateItem]);
  const deleteEmergencyContact = useCallback((id) => deleteItem('emergencyContacts', id), [deleteItem]);

  // Pets
  const getPets = useCallback(() => getItems('pets'), [getItems]);
  const addPet = useCallback((pet) => addItem('pets', pet), [addItem]);
  const updatePet = useCallback((id, updates) => updateItem('pets', id, updates), [updateItem]);
  const deletePet = useCallback((id) => deleteItem('pets', id), [deleteItem]);

  // Vehicles
  const getVehicles = useCallback(() => getItems('vehicles'), [getItems]);
  const addVehicle = useCallback((vehicle) => addItem('vehicles', vehicle), [addItem]);
  const updateVehicle = useCallback((id, updates) => updateItem('vehicles', id, updates), [updateItem]);
  const deleteVehicle = useCallback((id) => deleteItem('vehicles', id), [deleteItem]);

  // Maintenance Records
  const getMaintenanceRecords = useCallback(() => getItems('maintenanceRecords'), [getItems]);
  const addMaintenanceRecord = useCallback((record) => addItem('maintenanceRecords', record), [addItem]);
  const updateMaintenanceRecord = useCallback((id, updates) => updateItem('maintenanceRecords', id, updates), [updateItem]);
  const deleteMaintenanceRecord = useCallback((id) => deleteItem('maintenanceRecords', id), [deleteItem]);

  // Parking Locations
  const getParkingLocations = useCallback(() => getItems('parkingLocations'), [getItems]);
  const addParkingLocation = useCallback((location) => addItem('parkingLocations', location), [addItem]);
  const updateParkingLocation = useCallback((id, updates) => updateItem('parkingLocations', id, updates), [updateItem]);
  const deleteParkingLocation = useCallback((id) => deleteItem('parkingLocations', id), [deleteItem]);

  // Important Dates
  const getImportantDates = useCallback(() => getItems('importantDates'), [getItems]);
  const addImportantDate = useCallback((date) => addItem('importantDates', date), [addItem]);
  const updateImportantDate = useCallback((id, updates) => updateItem('importantDates', id, updates), [updateItem]);
  const deleteImportantDate = useCallback((id) => deleteItem('importantDates', id), [deleteItem]);

  // Contacts
  const getContacts = useCallback(() => getItems('contacts'), [getItems]);
  const addContact = useCallback((contact) => addItem('contacts', contact), [addItem]);
  const updateContact = useCallback((id, updates) => updateItem('contacts', id, updates), [updateItem]);
  const deleteContact = useCallback((id) => deleteItem('contacts', id), [deleteItem]);

  // Places
  const getPlaces = useCallback(() => getItems('places'), [getItems]);
  const addPlace = useCallback((place) => addItem('places', place), [addItem]);
  const updatePlace = useCallback((id, updates) => updateItem('places', id, updates), [updateItem]);
  const deletePlace = useCallback((id) => deleteItem('places', id), [deleteItem]);

  // Reimbursements
  const getReimbursements = useCallback(() => getItems('reimbursements'), [getItems]);
  const addReimbursement = useCallback((reimbursement) => addItem('reimbursements', reimbursement), [addItem]);
  const updateReimbursement = useCallback((id, updates) => updateItem('reimbursements', id, updates), [updateItem]);
  const deleteReimbursement = useCallback((id) => deleteItem('reimbursements', id), [deleteItem]);

  // Documents
  const getDocuments = useCallback(() => getItems('documents'), [getItems]);
  const addDocument = useCallback((document) => addItem('documents', document), [addItem]);
  const updateDocument = useCallback((id, updates) => updateItem('documents', id, updates), [updateItem]);
  const deleteDocument = useCallback((id) => deleteItem('documents', id), [deleteItem]);

  // Household
  const getHousehold = useCallback(() => demoData?.household || {}, [demoData]);
  const updateHousehold = useCallback((updates) => {
    updateDemoData(prev => ({
      ...prev,
      household: { ...prev.household, ...updates }
    }));
  }, [updateDemoData]);

  // Shopping Lists
  const getShoppingLists = useCallback(() => getItems('shoppingLists'), [getItems]);
  const addShoppingList = useCallback((list) => addItem('shoppingLists', list), [addItem]);
  const updateShoppingList = useCallback((id, updates) => updateItem('shoppingLists', id, updates), [updateItem]);
  const deleteShoppingList = useCallback((id) => deleteItem('shoppingLists', id), [deleteItem]);

  // Financial
  const getFinancial = useCallback(() => demoData?.financial || {}, [demoData]);
  const updateFinancial = useCallback((updates) => {
    updateDemoData(prev => ({
      ...prev,
      financial: { ...prev.financial, ...updates }
    }));
  }, [updateDemoData]);

  // End of Life
  const getEndOfLife = useCallback(() => demoData?.endOfLife || {}, [demoData]);
  const updateEndOfLife = useCallback((updates) => {
    updateDemoData(prev => ({
      ...prev,
      endOfLife: { ...prev.endOfLife, ...updates }
    }));
  }, [updateDemoData]);

  // Family Sharing
  const getFamilySharing = useCallback(() => demoData?.familySharing || {}, [demoData]);
  const updateFamilySharing = useCallback((updates) => {
    updateDemoData(prev => ({
      ...prev,
      familySharing: { ...prev.familySharing, ...updates }
    }));
  }, [updateDemoData]);

  // Caregiver Access
  const getCaregiverAccess = useCallback(() => demoData?.caregiverAccess || {}, [demoData]);
  const updateCaregiverAccess = useCallback((updates) => {
    updateDemoData(prev => ({
      ...prev,
      caregiverAccess: { ...prev.caregiverAccess, ...updates }
    }));
  }, [updateDemoData]);

  // ============= DEMO SETTINGS (for intercepting /api/settings) =============
  const getDemoSettings = useCallback(() => ({
    notifications_enabled: true,
    leave_time_alerts: true,
    leave_time_buffer_minutes: 15,
    preparation_alerts: true,
    critical_alerts: true,
    max_notifications_per_day: 2,
    onboarding_completed: true,
    first_week_checkin_shown: true, // Skip check-in in demo
    account_created_at: "2023-01-15T10:00:00Z"
  }), []);

  // ============= SHARED MEDICAL DATA SUMMARY =============
  // Mimics the structure returned by /api/shared-medical/summary

  const getSharedMedicalSummary = useCallback(() => {
    if (!demoData) return null;
    
    return {
      profile: demoData.medicalProfile,
      allergies: demoData.allergies || [],
      medications: demoData.medications || [],
      conditions: demoData.conditions || [],
      surgeries: demoData.surgeries || [],
      side_effects: [], // Not in demo data
      emergency_contacts: demoData.emergencyContacts || [],
      doctors: demoData.doctors || [],
      insurance: demoData.insurance || []
    };
  }, [demoData]);

  // ============= EMERGENCY BINDER COMPLETION =============

  const getBinderCompletion = useCallback(() => {
    if (!demoData) return { completion_percentage: 0, filled_sections: 0, total_sections: 7 };
    
    let filled = 0;
    const total = 7;
    
    if ((demoData.allergies || []).length > 0) filled++;
    if ((demoData.medications || []).length > 0) filled++;
    if ((demoData.conditions || []).length > 0) filled++;
    if ((demoData.emergencyContacts || []).length > 0) filled++;
    if ((demoData.doctors || []).length > 0) filled++;
    if ((demoData.insurance || []).length > 0) filled++;
    if (demoData.medicalProfile?.blood_type) filled++;
    
    return {
      completion_percentage: Math.round((filled / total) * 100),
      filled_sections: filled,
      total_sections: total
    };
  }, [demoData]);

  const value = {
    // State
    isDemoMode,
    loading,
    demoData,
    
    // Demo Mode Control
    enterDemoMode,
    exitDemoMode,
    resetDemoData,
    
    // User
    getDemoUser,
    
    // Generic CRUD
    getItems,
    getItem,
    addItem,
    updateItem,
    deleteItem,
    
    // Medical Profile
    getMedicalProfile,
    updateMedicalProfile,
    
    // Profiles
    getProfiles,
    addProfile,
    updateProfile,
    deleteProfile,
    
    // Allergies
    getAllergies,
    addAllergy,
    updateAllergy,
    deleteAllergy,
    
    // Medications
    getMedications,
    addMedication,
    updateMedication,
    deleteMedication,
    
    // Conditions
    getConditions,
    addCondition,
    updateCondition,
    deleteCondition,
    
    // Surgeries
    getSurgeries,
    addSurgery,
    updateSurgery,
    deleteSurgery,
    
    // Doctors
    getDoctors,
    addDoctor,
    updateDoctor,
    deleteDoctor,
    
    // Insurance
    getInsurance,
    addInsurance,
    updateInsurance,
    deleteInsurance,
    
    // Emergency Contacts
    getEmergencyContacts,
    addEmergencyContact,
    updateEmergencyContact,
    deleteEmergencyContact,
    
    // Pets
    getPets,
    addPet,
    updatePet,
    deletePet,
    
    // Vehicles
    getVehicles,
    addVehicle,
    updateVehicle,
    deleteVehicle,
    
    // Maintenance Records
    getMaintenanceRecords,
    addMaintenanceRecord,
    updateMaintenanceRecord,
    deleteMaintenanceRecord,
    
    // Parking
    getParkingLocations,
    addParkingLocation,
    updateParkingLocation,
    deleteParkingLocation,
    
    // Important Dates
    getImportantDates,
    addImportantDate,
    updateImportantDate,
    deleteImportantDate,
    
    // Contacts
    getContacts,
    addContact,
    updateContact,
    deleteContact,
    
    // Places
    getPlaces,
    addPlace,
    updatePlace,
    deletePlace,
    
    // Reimbursements
    getReimbursements,
    addReimbursement,
    updateReimbursement,
    deleteReimbursement,
    
    // Documents
    getDocuments,
    addDocument,
    updateDocument,
    deleteDocument,
    
    // Household
    getHousehold,
    updateHousehold,
    
    // Shopping Lists
    getShoppingLists,
    addShoppingList,
    updateShoppingList,
    deleteShoppingList,
    
    // Financial
    getFinancial,
    updateFinancial,
    
    // End of Life
    getEndOfLife,
    updateEndOfLife,
    
    // Family Sharing
    getFamilySharing,
    updateFamilySharing,
    
    // Caregiver Access
    getCaregiverAccess,
    updateCaregiverAccess,
    
    // Demo Settings
    getDemoSettings,
    
    // Summaries
    getSharedMedicalSummary,
    getBinderCompletion
  };

  return (
    <DemoContext.Provider value={value}>
      {children}
    </DemoContext.Provider>
  );
};

export default DemoContext;
